<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.vatPeriods',
    path: 'vat-periods',
    component: 'VATPeriods',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'VATPeriods',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const items = ref(null);
  const frequencyOptions = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'name',
      header: $fn.value.pgettext('VATPeriod', 'Name'),
      width: '160px',
      required: true,
    },
    {
      name: 'frequency',
      header: $fn.value.pgettext('VATPeriod', 'Frequency'),
      type: 'select',
      options: frequencyOptions.value,
      valueField: 'value',
      textField: 'name',
      width: '140px',
      required: true,
    },
    {
      name: 'start',
      header: $fn.value.pgettext('VATPeriod', 'Start'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'end',
      header: $fn.value.pgettext('VATPeriod', 'End'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'due_date',
      header: $fn.value.pgettext('VATPeriod', 'Due date'),
      width: '160px',
      type: 'date',
      required: true,
    },
    {
      name: 'closed',
      header: $fn.value.pgettext('VATPeriod', 'Closed'),
      type: 'checkbox',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    frequencyOptions: frequencyOptions.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_vat_periods');
  };
  const $$load_frequencyOptions = () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'vat_frequency_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'frequencyOptions' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_3343864469950044_columns',
        nodeId: '3343864469950044',
        dependsOn: ['frequencyOptions'],
      },
    ],
    loaders: { items: $$load_items, frequencyOptions: $$load_frequencyOptions },
    ux: {},
    dataRefs: {
      items,
      frequencyOptions,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_9yzwarrzn_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_cxce3ckq9_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('vat_period').upsert({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_cxce3ckq9_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_cxce3ckq9_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_0ldcaxp3o_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('vat_period').delete({ ...$event.data.row })
    );
  };
  const act_3343864469950044_script_0ldcaxp3o_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_0ldcaxp3o_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_9yzwarrzn =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_9yzwarrzn_getActionArgs,
    });
  const inst_evh_7315092382398562_close_aq7cgqwvv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_9yzwarrzn,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_aq7cgqwvv],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_i1u0h8azq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8cxh476z5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_i1u0h8azq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8cxh476z5],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_bs56iscw5 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_dwr5qjada =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_bs56iscw5,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_dwr5qjada],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_cxce3ckq9 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_cxce3ckq9_getActionArgs,
  });
  const inst_evh_3343864469950044_save_eeqawtcne =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_cxce3ckq9,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_eeqawtcne],
    event: 'save',
  });
  const act_3343864469950044_script_0ldcaxp3o = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_0ldcaxp3o_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_1ole0j0vl =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_0ldcaxp3o,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_1ole0j0vl],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_aq7cgqwvv,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8cxh476z5,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_dwr5qjada,
    reload: inst_evh_8320016629450276_clickPreventStop_dwr5qjada,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_eeqawtcne,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_1ole0j0vl,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_aq7cgqwvv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_aq7cgqwvv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8cxh476z5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8cxh476z5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_dwr5qjada = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_dwr5qjada.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_eeqawtcne = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_eeqawtcne.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_1ole0j0vl = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_1ole0j0vl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="VATPeriods"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ pgettext('Settings', 'VAT periods') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.VATPeriods"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
